/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AccountsApiService } from '../../modules/centralisedManagement/shared/http';
import { ActivatedRoute, Router } from '@angular/router';
import { AfterViewInit, Component, ElementRef, HostListener, Renderer2, ViewChild, } from '@angular/core';
import { AuthApiService } from '../../core/http';
import {
    AuthStoreService,
    ConnectionDetailsService,
    KeyStoreService,
    LanguageService,
    LoadingService,
    LogService,
    ModalService,
    NavigationService,
    NotifyService,
    PGPService,
    UserService,
    UtilsService,
    ValidationService
} from '../../core/services';
import { BaseComponent, LoadingButtonComponent } from '../../core/components';
import { ErrorResponseModel } from '../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { KeyStoreEnum } from '../../core/enums';
import { LicenceModel, LicenceService, LoginModel } from '../../modules/loadbalancer/shared';
import { PortalUrlsEnum } from '../../modules/centralisedManagement/shared/enums';
import { Subject } from 'rxjs';
import { domainOrIpValidation, portRangeValidation } from '../../core/directives';
import { takeUntil } from 'rxjs/operators';

// noinspection CssInvalidPseudoSelector
@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['login.component.scss', '../externalPageStyles.scss'],
})

export class LoginComponent extends BaseComponent implements AfterViewInit {
    /**
     * Reference to DOM button for sign in from the loading component.
     */
    @ViewChild(LoadingButtonComponent) public signInButton: ElementRef<LoadingButtonComponent>;

    /**
     * Reference to DOM button for sign in from the loading component.
     */
    @ViewChild('modalOK') public modalOkButton: ElementRef;

    /**
     * Store which logo to use for the view.
     */
    public companyLogoFile: 'light' | 'dark';

    /**
     * Welcome message.
     */
    public welcomeMessage: string;

    /**
     * Login form
     */
    public loginForm: FormGroup;

    /**
     * Login form
     */
    public samlLoginForm: FormGroup;

    /**
     * User login details model
     */
    public userDetails: LoginModel = <LoginModel>{};

    /**
     * User login error title
     */
    public errorTitle: string = '';

    /**
     * User login error message
     */
    public errorMessage: string;

    /**
     * User login error url
     */
    public errorUrl: string;

    /**
     * Force the accordion open
     */
    public forceOpen: boolean = false;

    /**
     * Has a single sign on token.
     */
    public hasSSOToken: boolean = false;

    /**
     * Store the sso user to display in the view.
     */
    public ssoUser: string = null;

    /**
     * Stored the user ID coming back from the login object.
     */
    public twoFactorUserDetails: any = null;

    /**
     * Hashed phone number from OTP service.
     */
    public twoFactorAuthPhoneNumber: string = '';

    /**
     * Saml enforced.
     */
    public samlEnforced: boolean = false;

    /**
     * To store the previous logged-in user.
     * @private
     */
    private readonly previousUser: string = '';

    /**
     * As we are not extending base we need reference to destroy.
     * @protected
     */
    protected destroyed$: Subject<boolean> = new Subject();

    constructor(
        public connectionDetailsService: ConnectionDetailsService,
        public modalService: ModalService,
        private accountsApiService: AccountsApiService,
        private activatedRoute: ActivatedRoute,
        private authHttpService: AuthApiService,
        private authStoreService: AuthStoreService,
        private formBuilder: FormBuilder,
        private keyStoreService: KeyStoreService,
        private languageService: LanguageService,
        private licenseService: LicenceService,
        private loadingService: LoadingService,
        private logService: LogService,
        private navigationService: NavigationService,
        private notifyService: NotifyService,
        private pgpService: PGPService,
        private renderer: Renderer2,
        private router: Router,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        this.setupFormGroups();
        this.setupFormSubscriber();

        if (this.utilsService.localStoragePresent()) {
            this.setSamlEnforcedState();
            this.patchConnections();
            this.setLogo();
        }

        const defaultClass = this.modulesEnum[this.environment.module];
        this.renderer.addClass(document.body, defaultClass.toLowerCase());
        this.checkForTokenInRoute();
        this.checkForNavigationExtras();
        this.previousUser = this.userService.user.email;

        this.setWelcomeMessage();

        this.clearNotificationsOnload();
    }

    ngAfterViewInit(): void {
        // This is best endeavours to get the login button to display the correct state after chrome has autofilled the form.
        const loadTimer = setTimeout(() => {
            this.clearValidatorOnAutoFill();
            clearTimeout(loadTimer);
        }, 650);
    }

    /**
     * Get accordion title.
     */
    public getAccordionTitle(): string {
        const path: string = 'assets/shared/icons/connection-settings.svg';

        return this.connectionDetailsService.isLoadbalancer()
            ? this.languageService.translate('login_connection_settings')
            : `<img src="${path}" height="20" width="20" alt="globe"/>`;
    }

    /**
     * Set the correct logo based on the film.
     */
    public setLogo(): void {
        const theme: string = localStorage.getItem('lbTheme');
        this.companyLogoFile = theme && theme.includes('dark')
            ? 'light'
            : 'dark';
    }

    /**
     * Send user to ping page and reload page.
     */
    public sendToPingPage(): void {
        const loginForm: FormGroup = this.loginForm;
        const connectionDetails = {
            userLogin: loginForm.get('login-email').value,
            password: loginForm.get('login-password').value,
            ip: loginForm.get('applianceAddress').value,
            port: loginForm.get('appliancePort').value
        };
        this.logService.debug('LoginComponent', 'sendToPingPage', connectionDetails);
        localStorage.setItem('lbHistory', JSON.stringify(connectionDetails));
        window.open(this.errorUrl, '_blank');
        location.reload();
    }

    /**
     * Redirect user to create account page.
     */
    public createAccount(): void {
        window.location.href = 'https://www.loadbalancer.org/get-started/adc-portal/';
    }

    /**
     * Redirect user to SSO sign in.
     * @param brand
     */
    public linkToSSOProvider(brand: string): void {
        if (brand === 'saml') {
            this.modalService.openModalInParent('saml');
        } else {
            window.location.href = this.environment.production
                ? '/api/v1/accounts/auth/' + brand
                : PortalUrlsEnum.NON_PRODUCTION + '/api/v1/accounts/auth/' + brand;
        }
    }

    /**
     * Login with SAML.
     */
    public loginWithSaml(): void {
        this.authHttpService.loginWithSaml(this.samlLoginForm.value)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                    next: (data: any): void => {
                        window.location.href = data || undefined;
                    },
                    error: (error: ErrorResponseModel): void => {
                        const timerId = setInterval((): void => { // UX only
                            this.logService.error(
                                'LoginComponent:loginWithSaml',
                                'Unable to get redirect for SAML IDP', error
                            );
                            this.notifyService.error('Unable to get redirect for SAML IDP');
                            this.modalService.close('saml');
                            this.loadingService.loadingButtonEmitter.next({ id: 'login-with-saml', state: false });
                            this.samlLoginForm.reset();
                            clearInterval(timerId);
                        }, 1500);
                    }
                }
            );
    }

    /**
     * Check for token in event (2FA Login).
     * @param $event
     */
    public checkForTokenInEvent($event: any): void {
        const data = $event.data;
        if (data?.token) {
            this.hasSSOToken = false;
            this.loginHelperFunction(data);
        }
    }

    /**
     * Process login
     */
    public login(): void {
        this.userDetails = this.formatUserDetails(this.loginForm.value);
        this.loginForm.disable();
        this.authHttpService.login(this.userDetails)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    if (data.otpEnabled) {
                        if (!data.id) {
                            this.notifyService.error('This service is currently down, please try again later');
                        } else {
                            this.enableTwoFactorAuthStep(this.userDetails, data);
                        }
                    } else {
                        this.setPrivateKeys(data);
                        // Set our user details
                        this.setUserLoginDetails(this.userDetails, data);
                        // Set our license details
                        this.setLoginLicenseDetails(data);

                        const location = navigator.language || data.language;
                        // Set userManagement default language
                        this.languageService.setLanguage(location);

                        // timer for UX improvements
                        const timer = setTimeout(
                            (): void => {
                                const firstPage: string = this.getFirstPage(data.appliance_id);
                                this.router.navigate([firstPage]).then().catch((): void => {
                                    this.loginForm.enable();
                                });
                                clearTimeout(timer);
                            }, 1000
                        );
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.loginForm.enable();
                    this.handleLoginError(error);
                    this.modalService.openModalInParent('loginError');
                    this.loadingService.loadingButtonEmitter.next({ id: 'login-sign-in', state: false });
                }
            });
    }

    /**
     * Add a host listener for checking the enter key.
     * @param event
     */
    @HostListener('document:keydown.enter', ['$event'])
    public onKeydownHandler(event: KeyboardEvent): void {
        // Stop other buttons being clicked.
        event.stopPropagation();
        event.preventDefault();

        if (event.key === 'Enter' && this.loginForm.valid) {
            const modal = this.modalService.findModal('loginError');
            const element: HTMLElement = this.getLoginButton();
            if (modal.isOpen) {
                this.modalOkButton.nativeElement.click();
            } else if (element) {
                element.click();
            } else {
                this.logService.warn('LoginComponent: onKeydownHandler', 'No button found');
            }
        } else {
            this.logService.warn('LoginComponent: onKeydownHandler', 'Form invalid');
        }
    }

    /**
     * Remove password from local storage after it has been set
     * @param connectionHistory
     * @private
     */
    private static removeLoginDetailsFromLBHistory(connectionHistory: LoginModel): void {
        delete connectionHistory.email;
        delete connectionHistory.password;

        localStorage.setItem('lbHistory', JSON.stringify(connectionHistory));
    }

    /**
     * Set welcome message.
     * @private
     */
    private setWelcomeMessage(): void {
        let message: string;
        switch (this.environment.module) {
            case this.modulesEnum.LOADBALANCER:
                message = 'welcome_to_endurance';
                break;
            case this.modulesEnum.CENTRALISED_MANAGEMENT:
                message = 'welcome_to_portal';
                break;
            case this.modulesEnum.DEEPTHOUGHT:
                message = 'welcome_to_admin_panel';
                break;
            default:
                message = 'No module found';
        }

        this.welcomeMessage = message;
    }

    /**
     * Clear existing notifications in case of being logged out through token expiry.
     * @private
     */
    private clearNotificationsOnload(): void {
        // Covers the edge case where we are logged out without calling the logout() function.
        const notifications = this.notifyService.notifications;

        if (notifications?.length) {
            this.notifyService.clearAll();
        }
    }

    /**
     * Check for token in route (SSO Login).
     * @private
     */
    private checkForTokenInRoute(): void {
        this.activatedRoute.queryParams
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (params: any) => {
                    if (params?.token) {
                        this.hasSSOToken = true;
                        this.loginForm.disable();
                        const data = { token: this.utilsService.duplicateObject(params.token) };
                        this.loginHelperFunction(data);
                    }
                },
                error: (error: ErrorResponseModel) => {
                    this.logService.warn(
                        'LoginComponent:checkForTokenInRoute',
                        'Unable to get query parameters', error);
                }
            });
    }

    /**
     * Check for navigation extras.
     * @private
     */
    private checkForNavigationExtras(): void {
        const navigationState: { email: string, password: string } = <{
            email: string,
            password: string
        }>this.router.getCurrentNavigation()?.extras?.state;
        if (navigationState?.email && navigationState?.password && navigationState?.password.length) {
            const timerId = setTimeout((): void => {
                this.loginForm.get('login-email').setValue(navigationState.email);
                this.loginForm.get('login-password').setValue(navigationState.password);
                this.loadingService.loadingButtonEmitter.next({ id: 'login-sign-in', state: true });
                this.login();
                clearTimeout(timerId);
            }, 50);
        }
    }

    /**
     * Login helper function
     * @param data
     * @private
     */
    private loginHelperFunction(data: { token: any; }): void {
        const decodedToken = this.utilsService.decodeJWT(data.token);
        const userDetails = this.createUserDetailsFromToken(decodedToken);
        this.ssoUser = decodedToken.email;
        const ux = setTimeout((): void => {
            this.setUserLoginDetails(userDetails, data);
            const location: string = navigator.language || 'en-us';
            this.languageService.setLanguage(location);
            this.setUpUserKeys(decodedToken.id?.toString());
            clearTimeout(ux);
        }, 1000);
    }

    /**
     * Create user details from token.
     * @param decodedToken
     * @private
     */
    private createUserDetailsFromToken(decodedToken: any): any {
        return {
            email: decodedToken.email,
            applianceAddress: this.connectionDetailsService.getAddress(),
            appliancePort: this.connectionDetailsService.getPort(),
            ssoLogin: this.hasSSOToken,
            otpEnabled: this.twoFactorUserDetails?.otpEnabled || false,
            ssoProvider: decodedToken.ssoProvider || null,
            tfaType: decodedToken.tfaType || null,
        };
    }

    /**
     * Get user keys from account.
     * @param id
     * @private
     */
    private setUpUserKeys(id: string): void {
        this.accountsApiService.get(id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(accountDetails => {
                this.setPrivateKeys(accountDetails);
                const cmFirstPage = this.userService.getUser('organisationId') > 0
                    ? 'cm/dashboard' : 'cm/onboard';
                this.router.navigate([cmFirstPage]).then().catch();
            });
    }

    /**
     * Get login button.
     * @private
     */
    private getLoginButton(): HTMLElement {
        return this.signInButton['buttonReference'].nativeElement;
    }

    /**
     * Format user details
     * @param loginFormValues
     * @private
     */
    private formatUserDetails(loginFormValues: any): LoginModel {
        const userDetails: LoginModel = {};
        userDetails.applianceAddress = loginFormValues.applianceAddress;
        userDetails.appliancePort = loginFormValues.appliancePort;
        userDetails['ssoLogin'] = this.hasSSOToken;
        userDetails.email = loginFormValues['login-email'];

        switch (this.environment.module) {
            case this.modulesEnum.CENTRALISED_MANAGEMENT:
                userDetails.password = this.pgpService.hashPassword(loginFormValues['login-password']);
                break;
            case this.modulesEnum.LOADBALANCER:
                userDetails.password = loginFormValues['login-password'];
                break;
            case this.modulesEnum.DEEPTHOUGHT:
                // TODO using portal atm
                userDetails.applianceAddress = 'kodiak.lborg.dev';
                userDetails.appliancePort = 443;
                userDetails.password = this.pgpService.hashPassword(loginFormValues['login-password']);
                break;
            default:
                userDetails.password = '';
        }

        return userDetails;
    }

    /**
     * Send two-factor auth code.
     * @param userDetails
     * @param data
     * @private
     */
    private enableTwoFactorAuthStep(userDetails: LoginModel, data: {
        id: any;
        otpEnabled: any;
        hashedNumber: string;
        token: string;
        tfaType: string,
    }): void {
        this.twoFactorUserDetails = userDetails;
        Object.assign(this.twoFactorUserDetails,
            {
                id: data.id,
                otpEnabled: data.otpEnabled,
                tfaType: data.tfaType,
                hashedNumber: data.hashedNumber
            }
        );

        this.twoFactorAuthPhoneNumber = data.hashedNumber;
    }

    /**
     * Return the first page from the login service.
     * @param applianceId
     * @private
     */
    private getFirstPage(applianceId: string): string {
        const overview: string = this.getOverviewLink(applianceId);

        const redirectUrl: string = this.navigationService.redirectLocation
            ? this.navigationService.redirectLocation
            : overview;

        return this.previousUser && this.previousUser !== this.userService.getUser('email')
            ? overview
            : redirectUrl;
    }

    /**
     * Get overview link.
     * @param applianceId
     * @private
     */
    private getOverviewLink(applianceId: string): string {
        switch (this.environment.module) {
            case this.modulesEnum.CENTRALISED_MANAGEMENT:
                return 'cm/dashboard';
            case this.modulesEnum.LOADBALANCER:
                return `lb/${applianceId}/overview`;
            case this.modulesEnum.DEEPTHOUGHT:
                return 'dt/dashboard';
            default:
                this.logService.debug('LoginComponent:getOverviewLink', 'No module matched to get link');
        }
    }

    /**
     * Handle login error
     * @param error
     * @private
     */
    private handleLoginError(error: any): void {
        this.errorTitle = 'login_error';
        this.errorMessage = this.getErrorMessage(error.message, this.userDetails);
        this.logService.debug('LoginComponent:Auth Failure', error);
        if (error.url?.length) {
            this.errorTitle = 'login_untrusted_host_certificate';
            this.errorUrl = error.url;
            if (this.userDetails.applianceAddress === 'localhost') {
                this.errorTitle = 'login_invalid_ip';
                this.errorMessage = 'You are trying to connect to localhost';
                this.forceOpen = true;
            }
        }
    }

    /**
     * Set the private keys.
     * @param data
     * @private
     */
    private setPrivateKeys(data: { privateKey: string; orgPrivateKey: string; }): void {
        if (data?.privateKey) {
            this.keyStoreService.isLoginProcess = true;
            this.keyStoreService.storeState = KeyStoreEnum.OPEN;
            this.keyStoreService.userPrivateKey = data.privateKey;
            if (data?.orgPrivateKey) {
                this.keyStoreService.orgPrivateKey = data.orgPrivateKey;
            }
            this.keyStoreService.isLoginProcess = false;
        } else {
            switch (this.environment.module) {
                case this.modulesEnum.CENTRALISED_MANAGEMENT:
                    this.logService.error('LoginComponent:setPrivateKeys', 'No key data available');
                    break;
                case this.modulesEnum.LOADBALANCER:
                    this.logService.debug('LoginComponent:setPrivateKeys', 'No key data needed');
                    break;
                case this.modulesEnum.DEEPTHOUGHT:
                    this.logService.debug('LoginComponent:setPrivateKeys', 'I don\'t know if we need keys');
                    break;
                default:
                    this.logService.debug('LoginComponent:setPrivateKeys', 'No module matched');

            }
        }
    }

    /**
     * Get error message depending on log in type
     * // UNIT TEST THIS
     * @param message
     * @param userDetails
     * @private
     */
    private getErrorMessage(message: string, userDetails: any): string {
        if (message?.length) {
            return message;
        }

        if (
            !userDetails.userLogin.includes('@')
        ) {
            return 'Your email address doesn\'t contain \'@\' please check and try again.';
        }
    }

    /**
     * Set up login parameters, helper function.
     * @param userDetails
     * @param data
     * @private
     */
    private setUserLoginDetails(userDetails: LoginModel, data: any): void {
        // Set the login type 'centralised or loadbalancer'
        this.authStoreService.setAuthModule(userDetails, data);
        // Set up user data
        this.userService.login(userDetails, data);
    }

    /**
     * Setup page form groups.
     * @private
     */
    private setupFormGroups(): void {
        this.loginForm = this.formBuilder.group({
            applianceAddress: [null, [Validators.required, domainOrIpValidation]],
            appliancePort: [null, [Validators.required, portRangeValidation]],
            'login-email': [null, [Validators.required, ValidationService.emailValidator]],
            'login-password': [null, Validators.required]
        });

        this.loginForm.enable();

        this.samlLoginForm = this.formBuilder.group({
            email: [null, [Validators.required, ValidationService.emailValidator]]
        });
    }

    /**
     * Set up form subscriber.
     * @private
     */
    private setupFormSubscriber(): void {
        this.loginForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: values => {
                    this.utilsService.debounce('applianceAddress', () => {
                        this.updateConnectionHistory(values.applianceAddress, values.appliancePort);
                        const loginEmail = this.loginForm.get('login-email');
                        if (loginEmail.value === '') {
                            loginEmail.setValidators([Validators.required, ValidationService.emailValidator]);
                            loginEmail.updateValueAndValidity({ emitEvent: false });
                        }

                        const loginPassword = this.loginForm.get('login-password');
                        if (loginPassword.value === '') {
                            loginPassword.setValidators([Validators.required]);
                            loginPassword.updateValueAndValidity({ emitEvent: false });
                        }
                    });
                }
            });
    }

    /**
     * Helper function to update the connection history.
     * @param address
     * @param port
     * @private
     */
    private updateConnectionHistory(address: any, port: any): void {
        const connectionHistory: any = this.connectionDetailsService.getConnHistLocalStorage();
        connectionHistory.ip = address;
        connectionHistory.port = port;
        localStorage.setItem('lbHistory', JSON.stringify(connectionHistory));
    }

    /**
     * Set SAML enforced state.
     * @private
     */
    private setSamlEnforcedState(): void {
        const lbHistory: string = localStorage.getItem('lbHistory');
        const historyObject: any = JSON.parse(lbHistory);
        this.samlEnforced = historyObject?.samlEnforced ? historyObject.samlEnforced : null;
    }

    /**
     * Patch connections history to form.
     * @private
     */
    private patchConnections(): void {
        const connectionHistory = this.connectionDetailsService.getConnHistLocalStorage();
        const connectionAddress = this.connectionDetailsService.getAddress();
        const connectionPort = this.connectionDetailsService.getPort();

        if (connectionHistory) {
            this.loginForm.controls['applianceAddress'].setValue(connectionAddress);
            this.loginForm.controls['appliancePort'].setValue(connectionPort);
            this.loginForm.controls['login-email'].setValue(connectionHistory.userLogin);
            this.loginForm.controls['login-password'].setValue(connectionHistory.password);
            LoginComponent.removeLoginDetailsFromLBHistory(connectionHistory);
        }
    }

    /**
     * Detect chrome autofilled inputs and clear the validation.
     * @private
     */
    private clearValidatorOnAutoFill(): void {
        const userAgent = navigator.userAgent;
        if (userAgent.match(/chrome|chromium|crios/i)) {
            const autoCompleteFields = document.querySelectorAll('input:-internal-autofill-selected');
            if (autoCompleteFields.length) {
                const inputsArray = Array.from(autoCompleteFields);
                for (const input of inputsArray) {
                    const formElement = this.loginForm.get(input.id);
                    if (formElement && formElement.enabled) {
                        formElement.clearValidators();
                        formElement.updateValueAndValidity();
                    }
                }
            }
        }
    }

    /**
     * Set our license details based on our login response
     * @param data
     * @private
     */
    private setLoginLicenseDetails(data: { licence: LicenceModel }): void {
        // Check to make sure we have license details
        if (!data.licence) {
            this.logService.error('' +
                'LoginComponent',
                'Authentication token failed to provide license details.'
            );
            this.errorMessage = 'Unable to verify licence details. Please contact loadbalancer.org support.';
        }
        // Set the licence notifications
        this.licenseService.licenceObj = <LicenceModel>data.licence;
    }
}
